import React from 'react';
import { SGSButton, SGSFormInput, SGSFormSelect, SGSFormWysiwyg } from 'components';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { rubriqueService, tagService } from 'core/services';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'core/hooks';
import { FormProps, isCreation, SGSForm } from 'components/Form';
import { QuestionForm } from 'core/models/FAQ';
import { useHistory } from 'react-router';

type Props = FormProps<QuestionForm>;

const FAQQuestionForm: React.FC<Props> = ({ defaultValues = {}, ...props }) => {
    const isCreate = isCreation(props);
    const onSubmit = isCreate ? props.onCreate : props.onEdit;
    const { t } = useTranslation();
    const history = useHistory();
    const validationSchema: Yup.SchemaOf<QuestionForm> = Yup.object().shape({
        title: Yup.string().required().max(255),
        content: Yup.string().required(),
        rubriqueId: Yup.mixed().required(),
        sortOrder: Yup.number().required(),
        tags: Yup.array().of(Yup.number()),
    });

    const methods = useForm<QuestionForm>({
        resolver: yupResolver(validationSchema),
        defaultValues,
    });
    const { handleSubmit } = methods;

    const { data: rubriques } = useQuery(rubriqueService.getRubriques, 'faq');
    const { data: tags } = useQuery(tagService.getTags);

    return (
        <FormProvider {...methods}>
            <SGSForm.Card onSubmit={handleSubmit(onSubmit)}>
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormInput variant="outlined" name="title" label="help.faq.question.title" />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Row>
                    <SGSForm.Col md={6}>
                        <SGSFormSelect
                            name="rubriqueId"
                            variant="outlined"
                            label="help.faq.question.rubrique"
                            options={
                                rubriques?.map((o) => ({
                                    value: o.id.toString(),
                                    label: o.name,
                                })) ?? []
                            }
                        />
                        <SGSFormInput
                            variant="outlined"
                            name="sortOrder"
                            label="help.faq.question.order"
                            type="number"
                        />
                    </SGSForm.Col>
                    <SGSForm.Col md={6}>
                        <SGSFormSelect
                            name="tags"
                            variant="outlined"
                            label="help.faq.question.tag"
                            multiple
                            options={
                                tags?.map((o) => ({
                                    value: o.id.toString(),
                                    label: o.name,
                                })) ?? []
                            }
                        />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormWysiwyg
                            name="content"
                            label="help.faq.question.content"
                            init={{
                                height: 300,
                                menubar: false,
                                plugins: [
                                    'advlist autolink lists link image',
                                    'charmap print preview anchor help',
                                    'searchreplace visualblocks code',
                                    'insertdatetime media table paste wordcount',
                                ],
                                toolbar:
                                    'undo redo | formatselect | image | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | help',
                            }}
                        />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Actions>
                    <SGSButton
                        variant="outlined"
                        className="mr-2"
                        onClick={() => {
                            history.push('/faq/questions');
                        }}
                    >
                        {t('global.back')}
                    </SGSButton>
                    <SGSButton type="submit">{isCreate ? t('global.create') : t('global.save')}</SGSButton>
                </SGSForm.Actions>
            </SGSForm.Card>
        </FormProvider>
    );
};

export default FAQQuestionForm;
